/* Per-student review drilldown (Phase 3 #1) — the instructor's "Review →" view.
 *
 * Mirrors the prototype's studentDetail panel: balanced-scorecard radar,
 * financial history chart, and a per-quarter log pairing each round's decision
 * bundle with the student's strategy journal. Read-only; grade overrides stay
 * on the Grading tab. Rendered inside the instructor console (.ic-root). */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from 'recharts';
import { useStudentReview } from '../hooks/queries';
import { Spinner, EmptyNote } from '../components/ui';
import { money } from '../lib/format';
import type { StudentReviewData } from '../lib/types';

const SC_LABELS: Record<string, string> = {
  profit: 'Profitability', profitability: 'Profitability', share: 'Market share',
  brand: 'Brand', quality: 'Quality', satisfaction: 'Satisfaction', csat: 'Satisfaction',
  ops: 'Operations', operations: 'Operations', innovation: 'Innovation',
  finance: 'Fin. health', financial: 'Fin. health',
};

/** Recharts tooltip surface — the opaque panel the other chart screens use.
 *  `--card` is a 4% white overlay on dark, so a tooltip painted with it was
 *  see-through over the chart lines. */
const TT = { background: 'var(--panel)', border: '1px solid var(--card-b)', borderRadius: 10, color: 'var(--txt)', fontSize: 12 } as const;

/** Compact human-readable summary of a round's decision bundle. */
function summarizeDecisions(d: Record<string, unknown>): string[] {
  if (!d || typeof d !== 'object') return [];
  const out: string[] = [];
  const n = (v: unknown) => (Array.isArray(v) ? v.length : 0);
  const productChanges = (d.products as any[] | undefined) ?? [];
  const priced = productChanges.filter((p) => p?.gens?.some((g: any) => g?.price != null)).length;
  const volumes = productChanges.filter((p) => p?.gens?.some((g: any) => g?.production != null)).length;
  const specs = productChanges.filter((p) => p?.newGen || p?.gens?.some((g: any) => g?.spec)).length;
  const mkt = productChanges.filter((p) => p?.mkt).length;
  if (n(d.newProducts)) out.push(`🆕 ${n(d.newProducts)} new product${n(d.newProducts) > 1 ? 's' : ''}`);
  if (specs) out.push(`⌚ ${specs} design change${specs > 1 ? 's' : ''}`);
  if (priced) out.push(`💲 repriced ${priced}`);
  if (volumes) out.push(`🏭 volumes for ${volumes}`);
  if (mkt) out.push(`📣 marketing for ${mkt}`);
  if (d.brandStrat) out.push(`🎨 brand → ${String(d.brandStrat)}`);
  if (d.suppliers && Object.keys(d.suppliers as object).length) out.push(`🔧 ${Object.keys(d.suppliers as object).length} supplier switch(es)`);
  if (d.rnd && (d.rnd as any).active !== undefined) out.push(`🔬 R&D → ${(d.rnd as any).active ?? 'idle'}`);
  if ((d.rnd as any)?.budget != null) out.push(`🔬 R&D budget ${money((d.rnd as any).budget)}`);
  if (n(d.distribution)) out.push(`🌐 ${n(d.distribution)} distribution change${n(d.distribution) > 1 ? 's' : ''}`);
  if (n(d.storeBuilds)) out.push(`🏪 stores in ${n(d.storeBuilds)} market${n(d.storeBuilds) > 1 ? 's' : ''}`);
  if (n(d.flagship)) out.push(`⭐ ${n(d.flagship)} flagship`);
  if (n(d.factoryBuilds)) out.push(`🏗️ ${n(d.factoryBuilds)} plant build${n(d.factoryBuilds) > 1 ? 's' : ''}`);
  if (n(d.factoryOps)) out.push(`🏭 ${n(d.factoryOps)} plant op${n(d.factoryOps) > 1 ? 's' : ''}`);
  if (n(d.contracts)) out.push(`🤝 ${n(d.contracts)} contract${n(d.contracts) > 1 ? 's' : ''}`);
  if (n(d.whBuilds)) out.push(`📦 ${n(d.whBuilds)} warehouse${n(d.whBuilds) > 1 ? 's' : ''}`);
  if (d.freightMode) out.push(`🚚 freight ${String(d.freightMode)}`);
  const fin = d.financing as any;
  if (fin) {
    if (n(fin.loans)) out.push(`🏦 ${n(fin.loans)} loan${n(fin.loans) > 1 ? 's' : ''}`);
    if (n(fin.bonds)) out.push(`📜 ${n(fin.bonds)} bond${n(fin.bonds) > 1 ? 's' : ''}`);
    if (fin.equityRaise) out.push(`📈 equity ${money(fin.equityRaise)}`);
    if (fin.buyback) out.push(`↩️ buyback ${money(fin.buyback)}`);
    if (fin.divRatio != null) out.push(`💵 dividends ${Math.round(fin.divRatio * 100)}%`);
  }
  return out.length ? out : ['No changes — carried last quarter’s decisions'];
}

export function StudentReview({ classId, userId, onBack }: { classId: string; userId: string; onBack: () => void }) {
  const q = useStudentReview(classId, userId);

  if (q.isLoading) return <Spinner label="Loading review…" />;
  if (q.isError || !q.data) return <EmptyNote>Couldn’t load this student’s review.</EmptyNote>;
  const r: StudentReviewData = q.data;

  const latestSc = [...r.rounds].reverse().find((x) => x.sc)?.sc ?? null;
  const radarData = latestSc
    ? Object.entries(latestSc).map(([k, v]) => ({ dim: SC_LABELS[k] ?? k, value: Math.round(v) }))
    : [];
  const chartData = r.rounds.map((x) => ({
    q: `Q${x.round}`,
    composite: x.composite != null ? Math.round(x.composite) : null,
    revenue: x.revenue != null ? +(x.revenue / 1e6).toFixed(2) : null,
    profit: x.profit != null ? +(x.profit / 1e6).toFixed(2) : null,
  }));
  const journalByRound = new Map(r.journals.map((j) => [j.round, j]));
  const quizAvg = (() => {
    const s = r.quizzes.map((x) => x.score).filter((x): x is number => x != null);
    return s.length ? s.reduce((a, b) => a + b, 0) / s.length : null;
  })();

  return (
    <div>
      <button className="btn btn-ghost btn-sm" onClick={onBack} style={{ marginBottom: 12 }}>← Back to class</button>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="spread" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h3 style={{ marginBottom: 2 }}>{r.student.name}</h3>
            <div className="sub">{r.student.email}{r.team ? <> · team {r.team.name}</> : null}</div>
          </div>
          <div className="sub" style={{ textAlign: 'right' }}>
            {r.game ? (
              <>
                <b style={{ color: 'var(--txt)' }}>{r.game.companyName}</b> · {r.game.strategy}
                <div>{r.game.status === 'finished' ? 'Finished' : `Quarter ${r.game.currentRound}`} / {r.game.maxRounds}
                  {r.game.composite != null ? <> · composite <b style={{ color: 'var(--acc)' }}>{Math.round(r.game.composite)}</b></> : null}
                  {r.grade?.total != null ? <> · grade <b style={{ color: 'var(--acc)' }}>{r.grade.total.toFixed(1)}</b></> : null}
                  {quizAvg != null ? <> · quiz avg {Math.round(quizAvg)}%</> : null}
                </div>
                {(() => {
                  const fin = [...r.rounds].reverse().find((x) => x.rating != null);
                  if (!fin && !r.game.insolvent) return null;
                  return (
                    <div>
                      <b style={{ color: 'var(--txt)' }}>Finance:</b>
                      {fin?.rating ? <> rating {fin.rating}</> : null}
                      {fin?.leverage != null ? <> · D/E {fin.leverage.toFixed(2)}×</> : null}
                      {(fin?.loan ?? 0) > 0 ? <> · overdraft {money(fin!.loan)}</> : null}
                      {r.game.insolvent ? <> · <span style={{ color: 'var(--bad)' }}>⚠ insolvency watch{r.game.insolventSince ? ` since Q${r.game.insolventSince}` : ''}</span></> : null}
                    </div>
                  );
                })()}
              </>
            ) : ('No game started yet')}
          </div>
        </div>
      </div>

      {r.rounds.length > 0 && (
        <div className="grid-2" style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', marginBottom: 14 }}>
          {radarData.length > 0 && (
            <div className="card">
              <h3>Balanced scorecard — latest quarter</h3>
              <div style={{ height: 260 }}>
                <ResponsiveContainer>
                  <RadarChart data={radarData} outerRadius="70%">
                    <PolarGrid stroke="var(--rule-strong)" />
                    <PolarAngleAxis dataKey="dim" tick={{ fill: 'var(--mut)', fontSize: 11 }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                    <Radar dataKey="value" stroke="var(--acc)" fill="var(--acc)" fillOpacity={0.25} />
                    <Tooltip contentStyle={TT} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          <div className="card">
            <h3>Financial history</h3>
            <div style={{ height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
                  <CartesianGrid stroke="var(--rule-faint)" />
                  <XAxis dataKey="q" tick={{ fill: 'var(--mut)', fontSize: 11 }} />
                  <YAxis yAxisId="l" tick={{ fill: 'var(--mut)', fontSize: 11 }} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fill: 'var(--mut)', fontSize: 11 }} />
                  <Tooltip contentStyle={TT} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="l" dataKey="composite" name="Composite" stroke="var(--acc)" dot={false} strokeWidth={2} />
                  <Line yAxisId="r" dataKey="revenue" name="Revenue ($M)" stroke="var(--acc2)" dot={false} />
                  <Line yAxisId="r" dataKey="profit" name="Profit ($M)" stroke="var(--good)" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <h3>Quarter-by-quarter — decisions &amp; journal</h3>
        {r.rounds.length === 0 ? (
          <EmptyNote>No quarters resolved yet.</EmptyNote>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {[...r.rounds].reverse().map((x) => {
              const j = journalByRound.get(x.round);
              return (
                <div key={x.round} style={{ border: '1px solid var(--rule-faint)', borderRadius: 10, padding: '10px 12px' }}>
                  <div className="spread" style={{ flexWrap: 'wrap', gap: 6 }}>
                    <b>Q{x.round}</b>
                    <span className="sub">
                      revenue {money(x.revenue)} · profit <span className={(x.profit ?? 0) >= 0 ? 'pos' : 'neg'}>{money(x.profit)}</span>
                      {x.share != null ? <> · share {(x.share * 100).toFixed(1)}%</> : null}
                      {x.composite != null ? <> · score <b style={{ color: 'var(--acc)' }}>{Math.round(x.composite)}</b></> : null}
                      {(x.odPenalty ?? 0) > 0 ? <> · <span className="neg">overdraft −{x.odPenalty} finance pts</span></> : null}
                      {x.insolvent ? <> · <span className="neg">⚠ insolvency watch</span></> : null}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '8px 0 2px' }}>
                    {summarizeDecisions(x.decisions).map((s, i) => (
                      <span key={i} className="chip" style={{ fontSize: 11 }}>{s}</span>
                    ))}
                  </div>
                  {j && (
                    <div className="sub" style={{ marginTop: 8, borderLeft: '2px solid var(--acc)', paddingLeft: 8, whiteSpace: 'pre-wrap' }}>
                      <b style={{ color: 'var(--txt)' }}>Journal:</b> {j.text}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {r.journals.some((j) => j.round == null || !r.rounds.find((x) => x.round === j.round)) && (
          <div style={{ marginTop: 10 }}>
            <h3 style={{ fontSize: 13 }}>Other journal entries</h3>
            {r.journals.filter((j) => j.round == null || !r.rounds.find((x) => x.round === j.round)).map((j) => (
              <div key={j.id} className="sub" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
                <b>Q{j.round ?? '—'}:</b> {j.text}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
